import { DappApiApi as DialbunnyApi } from './apis/generated/dialbunny/apis/DappApiApi';
import { Configuration, ResponseError } from './apis/generated/common/runtime';
import type { Auth } from './auth';
import { UV_HTTP_URL, POWER_DIALER_URL } from './constants';
import { DialpadContactApi } from './apis/generated/dialpad-contact';
import { inject } from 'vue';
import type { App, Plugin } from 'vue';
import type { AuthController, DPOAuth } from '@dialpad/auth';
import logger from './logger';

export type PowerDialerApis = {
  dialbunnyApi: DialbunnyApi;
  contactApi: DialpadContactApi;
};

const log = logger.getChild('api');

const DEFAULT_INJECTION_KEY = Symbol('power-dialer-apis');

const apiPlugin: Plugin<{ apis: PowerDialerApis }> = {
  install(app, { apis }) {
    log.log('installing apis plugin');

    app.provide(DEFAULT_INJECTION_KEY, apis);
  },
};

function apiFactory(authController: AuthController, dpOAuth: DPOAuth) {
  const configFactory = (url: string): Configuration =>
    new Configuration({
      basePath: url,
      middleware: [
        {
          pre: async (ctx) => {
            log.log(`Request ${ctx.init.method} ${ctx.url}.`);

            try {
              ctx.init.headers = {
                ...ctx.init.headers,
                Authorization: authController.getToken(),
              };
            } catch (error: unknown) {
              // getToken can fail, we want to return proper ResponseError so the caller can handle it
              throw new ResponseError(
                new Response(null, { status: 555 }),
                error?.toString(),
              );
            }
          },
          post: async (ctx) => {
            log.log(
              `Response ${ctx.init.method} ${ctx.url}. Status: ${ctx.response.status}.`,
            );

            if (ctx.response.status === 401) {
              log.log('Unauthorized. Logging out.');
              const authData = await authController.signOut();
              dpOAuth.redirectToSignout(authData);
            }
            return ctx.response;
          },
          onError: async (ctx) => {
            // we don't want to handle aborted requests here
            // it's responsible of the caller to handle it
            if (ctx.error === 'The operation was aborted.') return;

            log.warn(
              `Error ${ctx.init.method} ${ctx.url}. Status: ${
                ctx.response?.status ?? 'unknown'
              }.`,
            );

            return ctx.response ?? new Response(null, { status: 555 });
          },
        },
      ],
    });

  const dialbunnyConfig = configFactory(POWER_DIALER_URL);
  const dialpadConfig = configFactory(UV_HTTP_URL);

  const apis: PowerDialerApis = {
    dialbunnyApi: new DialbunnyApi(dialbunnyConfig),
    contactApi: new DialpadContactApi(dialpadConfig),
  };

  return apis;
}

export const hostPowerDialerApis = (context: {
  env: { POWER_DIALER_URL: string; UV_HTTP_URL: string };
  auth: Auth;
  vueApp: App;
}): { apis: PowerDialerApis } => {
  const apis = apiFactory(context.auth.authController, context.auth.dpOAuth);
  context.vueApp.use(apiPlugin, { apis });

  return { apis };
};

export const useApis = (): PowerDialerApis => {
  const apis = inject<PowerDialerApis>(DEFAULT_INJECTION_KEY);

  if (!apis) {
    throw new Error('useApis must be used within a vue context');
  }

  return apis;
};
